import React from "react";

const Privacy_Policy = () => {
  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Privacy Policy</h1>

      <p style={styles.paragraph}>
        <strong>Last updated:</strong> {new Date().getFullYear()}
      </p>

      <h2 style={styles.subheading}>1. Introduction — scope and acceptance</h2>
      <p style={styles.paragraph}>
        Funde Technologies (“we”, “us”, “our”) is committed to protecting and
        respecting your privacy. This Privacy Policy explains how we collect,
        use, disclose and protect personal information when you visit or use the
        Site, Apps or Platform (collectively, the “Service”).
      </p>
      <p style={styles.paragraph}>
        By using the Service you accept the practices described in this Privacy
        Policy. If you do not agree, please do not use our Service.
      </p>

      <h2 style={styles.subheading}>2. Controller and contact</h2>
      <p style={styles.paragraph}>
        Funde Technologies is the data controller for personal information
        processed under this Policy. For questions, requests to update or delete
        your information, or to withdraw consent, contact us at{" "}
        <strong>privacy@headwayspot.com</strong>.
      </p>
      <p style={styles.paragraph}>
        If you prefer postal communication, send mail to the registered address
        below and include your full name and email.
      </p>

      <h2 style={styles.subheading}>3. What information we collect</h2>
      <p style={styles.paragraph}>
        We collect two categories of information:
      </p>

      <h3 style={styles.minorHeading}>
        a) Non-personal (anonymous) information
      </h3>
      <ul style={styles.list}>
        <li>Device and browser type, screen resolution, operating system</li>
        <li>Usage and analytics data (pages visited, time spent, features used)</li>
        <li>IP-derived geolocation (city/region level only)</li>
        <li>Clickstream and aggregated performance data</li>
        <li>Error reports, diagnostic logs and voluntary feedback</li>
      </ul>

      <h3 style={styles.minorHeading}>
        b) Personal information (PII)
      </h3>
      <ul style={styles.list}>
        <li>Name, email address, job title, company name, phone number</li>
        <li>Billing and payment details (handled by payment providers)</li>
        <li>Account credentials and profile information</li>
        <li>Content uploaded to the Platform (documents, messages, metadata)</li>
        <li>Information provided via support requests or forms</li>
      </ul>

      <p style={styles.paragraph}>
        Non-Personal Information is treated as personal information if combined
        with PII.
      </p>

      <h2 style={styles.subheading}>4. How we collect information</h2>
      <ul style={styles.list}>
        <li>Directly from you when you register, contact support, or upload data</li>
        <li>Automatically via cookies, analytics, and server logs</li>
        <li>
          From third parties you connect to the Service (e.g., Google, Facebook)
        </li>
        <li>From other users who invite you to the Service</li>
      </ul>

      <h2 style={styles.subheading}>5. Cookies and similar technologies</h2>
      <p style={styles.paragraph}>
        We use cookies to provide core functionality, remember preferences, and
        produce aggregated analytics. Session and persistent cookies may be
        used. We do not use behavioural advertising cookies.
      </p>
      <p style={styles.paragraph}>
        You may block cookies via your browser, but doing so may affect Service
        functionality.
      </p>

      <h2 style={styles.subheading}>6. Do Not Track (DNT)</h2>
      <p style={styles.paragraph}>
        We honour Do Not Track signals where technically feasible and do not
        engage in advertising tied to such signals.
      </p>

      <h2 style={styles.subheading}>
        7. Third-party services and integrations
      </h2>
      <ul style={styles.list}>
        <li>Authentication: Google OAuth 2.0</li>
        <li>Payments & billing: Razorpay, Zoho</li>
        <li>Cloud providers: AWS, Google Cloud, Microsoft Azure</li>
        <li>Analytics: Google Analytics</li>
        <li>Support & chat: Crisp or similar tools</li>
        <li>
          AI/ML services: OpenAI, Google Bard, Meta Llama, Nanonets (where enabled)
        </li>
      </ul>

      <p style={styles.paragraph}>
        We do not sell personal information. Data is shared only to provide the
        Service under confidentiality and security obligations.
      </p>

      <h2 style={styles.subheading}>8. Legal use of data & aggregation</h2>
      <p style={styles.paragraph}>
        Client Data belongs to the Client organization. We may generate
        aggregated, de-identified datasets which do not identify individuals and
        may be used for business purposes.
      </p>

      <h2 style={styles.subheading}>9. Use of your information</h2>
      <ul style={styles.list}>
        <li>Operate, maintain, and improve the Service</li>
        <li>Authenticate users and manage access</li>
        <li>Process payments via providers</li>
        <li>Communicate updates, billing, and support</li>
        <li>Security, analytics, and legal compliance</li>
      </ul>

      <h2 style={styles.subheading}>
        10. Document processing and AI models
      </h2>
      <p style={styles.paragraph}>
        Uploaded documents may be processed using third-party AI services when
        features are enabled. Avoid uploading highly sensitive data unless
        required.
      </p>

      <h2 style={styles.subheading}>11. Payment & credit card data</h2>
      <p style={styles.paragraph}>
        We do not store full payment card details. Payments are processed
        securely by third-party providers and governed by their policies.
      </p>

      <h2 style={styles.subheading}>12. Security</h2>
      <p style={styles.paragraph}>
        We apply reasonable safeguards, but no system is completely secure. We
        will notify affected users of breaches as required by law.
      </p>

      <h2 style={styles.subheading}>13. Data retention</h2>
      <p style={styles.paragraph}>
        Data is retained only as long as necessary for Service delivery, legal
        compliance, dispute resolution, and legitimate business purposes.
      </p>

      <h2 style={styles.subheading}>14. International transfers</h2>
      <p style={styles.paragraph}>
        Data may be processed outside your country with appropriate safeguards
        in place.
      </p>

      <h2 style={styles.subheading}>
        15. Disclosure required by law; business transfers
      </h2>
      <p style={styles.paragraph}>
        We may disclose data when legally required or during mergers,
        acquisitions, or asset transfers.
      </p>

      <h2 style={styles.subheading}>16. Children</h2>
      <p style={styles.paragraph}>
        The Service is not intended for children under 16. Contact us if you
        believe such data has been collected.
      </p>

      <h2 style={styles.subheading}>17. Your rights and choices</h2>
      <p style={styles.paragraph}>
        You may request access, correction, deletion, restriction, portability,
        or withdrawal of consent by contacting privacy@headwayspot.com.
      </p>

      <h2 style={styles.subheading}>
        18. Complaints and dispute resolution
      </h2>
      <p style={styles.paragraph}>
        Contact us first with privacy concerns. You may also lodge complaints
        with a supervisory authority where applicable.
      </p>

      <h2 style={styles.subheading}>19. Marketing preferences</h2>
      <p style={styles.paragraph}>
        You can opt out of marketing emails via unsubscribe links or by emailing
        us.
      </p>

      <h2 style={styles.subheading}>20. Third-party links</h2>
      <p style={styles.paragraph}>
        We are not responsible for third-party privacy practices. Review their
        policies before sharing data.
      </p>

      <h2 style={styles.subheading}>21. Changes to this policy</h2>
      <p style={styles.paragraph}>
        Updates will be posted on this page and notified where required.
        Continued use constitutes acceptance.
      </p>

      <h2 style={styles.subheading}>22. Data protection contact</h2>
      <p style={styles.paragraph}>
        <strong>Email:</strong> privacy@headwayspot.com
        <br />
        <strong>Postal:</strong> Plot number 2/5, Bhagwan Nagar, Pathardi, Dist.
        – AhilyaNagar, 414102, Maharashtra, India
      </p>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "40px 20px",
    lineHeight: "1.7",
    color: "#1f2937",
  },
  heading: {
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "20px",
  },
  subheading: {
    fontSize: "20px",
    fontWeight: "600",
    marginTop: "28px",
    marginBottom: "12px",
  },
  minorHeading: {
    fontSize: "17px",
    fontWeight: "600",
    marginTop: "16px",
    marginBottom: "8px",
  },
  paragraph: {
    fontSize: "16px",
    marginBottom: "14px",
  },
  list: {
    paddingLeft: "20px",
    marginBottom: "16px",
  },
};

export default Privacy_Policy;